// src/components/storico/StoricoTable.tsx
import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { Table, Badge, Text, Group, ScrollArea, Box, Tooltip } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { Storico } from '../../types/storico-types';
import { ColumnResizer } from '../data-table/ColumnResizer';
import { RESIZABLE_TABLE_OPTIONS, useColumnSizing } from '../../hooks/use-column-sizing';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface StoricoTableProps {
  data: Storico[];
  onRowClick?: (storico: Storico) => void;
}

export function StoricoTable({ data, onRowClick }: StoricoTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'dataCompletamento', desc: true }
  ]);
  const { columnSizing, onColumnSizingChange } = useColumnSizing('storico');

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return '-';
    }
  };

  const getStatoBadgeColor = (stato?: string) => {
    if (!stato) return 'gray';

    const statoLower = stato.toLowerCase();
    if (statoLower.includes('completato') || statoLower.includes('completed')) return 'green';
    if (statoLower.includes('progress') || statoLower.includes('corso')) return 'blue';
    if (statoLower.includes('sospeso') || statoLower.includes('hold')) return 'yellow';
    if (statoLower.includes('annullato') || statoLower.includes('cancelled')) return 'red';
    return 'gray';
  };

  const columns = useMemo<ColumnDef<Storico>[]>(
    () => [
      {
        accessorKey: 'categoria',
        header: 'Categoria',
        size: 160,
        cell: ({ getValue }) => (
          <Text size="sm" fw={500}>{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        accessorKey: 'numDecoro',
        header: 'Num. Decoro',
        size: 130,
        cell: ({ getValue }) => (
          <Text size="sm" c="dimmed">{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        accessorKey: 'nomeDecoro',
        header: 'Nome Decoro',
        size: 300,
        cell: ({ getValue }) => {
          const value = (getValue() as string) || '-';
          return (
            <Tooltip label={value} multiline w={350} withArrow disabled={!value || value.length < 50}>
              <Text size="sm" lineClamp={2}>
                {value}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'formaBianco',
        header: 'Forma Bianco',
        size: 150,
        cell: ({ getValue }) => (
          <Text size="sm" c="dimmed">{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        accessorKey: 'statoProgetto',
        header: 'Stato Progetto',
        size: 170,
        cell: ({ getValue }) => {
          const stato = getValue() as string;
          return stato ? (
            <Badge variant="filled" color={getStatoBadgeColor(stato)} size="sm" style={{ color: 'white' }}>
              {stato}
            </Badge>
          ) : (
            <Text size="sm" c="dimmed">-</Text>
          );
        },
      },
      {
        accessorKey: 'dataCompletamento',
        header: 'Data Completamento',
        size: 170,
        cell: ({ getValue }) => (
          <Text size="sm">{formatDate(getValue() as string)}</Text>
        ),
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'tipologiaStatistica',
        header: 'Tipologia',
        size: 160,
        cell: ({ getValue }) => (
          <Text size="sm" c="dimmed">{(getValue() as string) || '-'}</Text>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    ...RESIZABLE_TABLE_OPTIONS,
    state: { sorting, columnSizing },
    onSortingChange: setSorting,
    onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <ScrollArea>
      <Table striped highlightOnHover withTableBorder withColumnBorders layout="fixed" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();

                return (
                  <Table.Th
                    key={header.id}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      cursor: canSort ? 'pointer' : 'default',
                      userSelect: canSort ? 'none' : 'auto',
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    <Group gap={4} wrap="nowrap">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <Box style={{ width: 16, display: 'flex', alignItems: 'center' }}>
                          {sortDirection === 'asc' && <IconChevronUp size={14} />}
                          {sortDirection === 'desc' && <IconChevronDown size={14} />}
                        </Box>
                      )}
                    </Group>
                    <ColumnResizer header={header} />
                  </Table.Th>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {data.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text ta="center" c="dimmed" py="xl">
                  Nessun dato trovato
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Table.Tr
                key={row.id}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Td key={cell.id} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}
